
// StoreListPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppFooter from "../components/AppFooter";
import { auth } from "../config/config-firebase";
import API_BASE from "../config/api";
import useMerchantStores, {
  getStoreId,
  isDefaultStoreRow as isDefaultRow,
} from "../hooks/useMerchantStores";
import {
  Plus, Store, ArrowLeft, Info, Pencil, Trash2, Check, X, Star,
} from "lucide-react";

const AVATAR_COLORS = [
  { bg: "bg-blue-50", text: "text-blue-700" },
  { bg: "bg-green-50", text: "text-green-700" },
  { bg: "bg-yellow-50", text: "text-yellow-700" },
  { bg: "bg-purple-50", text: "text-purple-700" },
  { bg: "bg-orange-50", text: "text-orange-700" },
];

const emptyForm = {
  storeName: "",
  storeLocation: "",
};

export default function StoreListPage() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null); // Firebase uid, used to key merchantDetails/getMerchantStoreDetails lookups
  const [actionError, setActionError] = useState("");

  // Add / edit modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingStore, setEditingStore] = useState(null); // null => add mode
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    stores,
    loadingStores,
    numericUserId,
    switchingId,
    error: storesError,
    setError: setStoresError,
    fetchStores,
    handleSetDefault,
    isTeamMember,
    isTeamMemberActive,
  } = useMerchantStores(userId);

  const displayError = actionError || storesError;
  const dismissError = () => {
    setActionError("");
    setStoresError("");
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      const uid = currentUser?.uid || localStorage.getItem("customer_id");
      setUserId(uid || null);
    });
    return () => unsubscribe();
  }, []);

  // Team members don't get a My Stores page — redirect silently (no error
  // messaging, as if the route doesn't exist) once we know the role.
  // A *disabled* team member is redirected to /login by the hook itself
  // (it owns that navigation), so this only handles active members.
  useEffect(() => {
    if (!loadingStores && isTeamMember && isTeamMemberActive) {
      navigate("/user-settings", { replace: true });
    }
  }, [loadingStores, isTeamMember, isTeamMemberActive, navigate]);

  if (!loadingStores && isTeamMember && isTeamMemberActive) {
    return null;
  }

  // ---------- Add / Edit ----------
  const openAddModal = () => {
    setEditingStore(null);
    setForm(emptyForm);
    setActionError("");
    setShowFormModal(true);
  };

  const openEditModal = (store) => {
    setEditingStore(store);
    setForm({
      storeName: store.full_name || "",
      storeLocation: store.address || "",
    });
    setActionError("");
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setEditingStore(null);
    setForm(emptyForm);
    setActionError("");
  };

  const handleSubmitForm = async () => {
    if (!form.storeName.trim() || !form.storeLocation.trim()) return;

    const isEdit = !!editingStore;
    if (!isEdit && !numericUserId) {
      setActionError("Still loading your account details — try again in a moment.");
      return;
    }
    if (isEdit && !getStoreId(editingStore)) {
      setActionError("This store isn't linked yet and can't be edited here. Contact support.");
      return;
    }

    setSaving(true);
    setActionError("");
    try {
      const url = isEdit
        ? `${API_BASE}/updateStoreDetails`
        : `${API_BASE}/saveStore`;

      // updateStoreDetails does NOT take userId per the API collection.
      const payload = isEdit
        ? {
            storeId: Number(getStoreId(editingStore)),
            storeName: form.storeName.trim(),
            storeLocation: form.storeLocation.trim(),
          }
        : {
            storeName: form.storeName.trim(),
            storeLocation: form.storeLocation.trim(),
            userId: Number(numericUserId),
          };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to save store.");
      }

      setShowFormModal(false);
      setEditingStore(null);
      setForm(emptyForm);
      await fetchStores();
    } catch (err) {
      console.error("Error saving store:", err);
      setActionError(err.message || "Failed to save store.");
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete ----------
  // Business rule: the default store can NEVER be deleted (edit-only).
  // Every non-default store can always be deleted, no other restriction.
  const handleDeleteStore = async () => {
    if (!deleteTarget) return;
    if (isDefaultRow(deleteTarget)) {
      setActionError("Your default store can't be deleted. Set another store as default first.");
      return;
    }
    if (!getStoreId(deleteTarget)) {
      setActionError("This store isn't linked yet and can't be deleted here. Contact support.");
      return;
    }
    if (!numericUserId) {
      setActionError("Still loading your account details — try again in a moment.");
      return;
    }

    setDeleting(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/deleteStore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeId: Number(getStoreId(deleteTarget)),
          userId: Number(numericUserId),
        }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to delete store.");
      }

      setDeleteTarget(null);
      await fetchStores();
    } catch (err) {
      console.error("Error deleting store:", err);
      setActionError(err.message || "Failed to delete store.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col w-full">
      {/* HEADER */}
      <div className="bg-white px-4 pt-5 pb-4 border-b border-gray-100 shadow-sm flex items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/user-settings")}
            className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full"
          >
            <ArrowLeft size={13} /> Back
          </button>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">My stores</h1>
            <p className="text-xs text-gray-500">Manage all your locations</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="flex items-center gap-1.5 bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded-full"
        >
          <Plus size={13} /> Add store
        </button>
      </div>

      {/* BODY */}
      <div className="px-4 pt-4 pb-28 flex-1 max-w-lg w-full mx-auto">
        {displayError && !showFormModal && !deleteTarget && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3 flex gap-2.5 items-start mb-3">
            <Info size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 leading-relaxed flex-1">{displayError}</p>
            <button type="button" onClick={dismissError} aria-label="Dismiss">
              <X size={14} className="text-red-400" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-bold text-gray-900">Stores</span>
          <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {stores.length}
          </span>
        </div>

        {loadingStores ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 flex flex-col items-center text-center">
            <p className="text-sm text-gray-400">Loading your stores...</p>
          </div>
        ) : stores.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
              <Store size={26} className="text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">No stores yet</p>
            <p className="text-xs text-gray-400 mb-5 max-w-xs">
              Add your first store location to manage screens and menus per location.
            </p>
            <button
              type="button"
              onClick={openAddModal}
              className="bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-full flex items-center gap-2"
            >
              <Plus size={15} /> Create store
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {stores.map((store, index) => {
              const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
              const storeId = getStoreId(store);
              const isDefault = isDefaultRow(store);

              return (
                <div
                  key={storeId ?? index}
                  className={`bg-white rounded-2xl shadow-sm p-4 border ${
                    isDefault ? "border-blue-300 ring-1 ring-blue-100" : "border-gray-100"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color.bg}`}
                    >
                      <Store size={20} className={color.text} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {store.full_name}
                        </p>
                        {isDefault && (
                          <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                            <Star size={9} className="fill-current" /> Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 truncate">
                        {store.address || "No location added"}
                      </p>
                      {isDefault && (
                        <p className="text-[10px] text-gray-300 mt-0.5">
                          Your default store can't be deleted — editing is still available
                        </p>
                      )}
                    </div>

                    {/* Edit is always available. Delete is only available
                        on non-default stores (business rule: default store
                        is edit-only, never deletable). */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => openEditModal(store)}
                        className="border border-gray-200 p-1.5 rounded-lg"
                        aria-label="Edit store"
                      >
                        <Pencil size={13} className="text-gray-400" />
                      </button>
                      {!isDefault && (
                        <button
                          type="button"
                          onClick={() => {
                            setDeleteTarget(store);
                            setActionError("");
                          }}
                          className="bg-red-50 p-1.5 rounded-lg"
                          aria-label="Delete store"
                        >
                          <Trash2 size={13} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>

                  {!isDefault && (
                    <button
                      type="button"
                      onClick={() => handleSetDefault(store)}
                      disabled={switchingId === storeId || !numericUserId}
                      className="mt-3 w-full border border-blue-200 text-blue-600 text-xs font-semibold py-2 rounded-full disabled:opacity-50"
                    >
                      {switchingId === storeId ? "Switching..." : "Set as default"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        type="button"
        onClick={openAddModal}
        className="fixed bottom-20 right-4 bg-blue-600 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-30"
      >
        <Plus size={22} className="text-white" />
      </button>

      {/* ADD / EDIT MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-8">
          <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl px-5 pt-3 pb-8 shadow-2xl">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />

            <div className="flex justify-between items-center mb-5">
              <h2 className="text-base font-bold text-gray-900">
                {editingStore ? "Edit store" : "Add store"}
              </h2>
              <button type="button" onClick={closeFormModal} className="p-0.5" disabled={saving}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {actionError && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
                {actionError}
              </p>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Store name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Downtown Branch"
                  value={form.storeName}
                  onChange={(e) => setForm((p) => ({ ...p, storeName: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600 font-medium mb-1 block">
                  Location
                </label>
                <input
                  type="text"
                  placeholder="e.g. 123 Main Street, Yonkers"
                  value={form.storeLocation}
                  onChange={(e) => setForm((p) => ({ ...p, storeLocation: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleSubmitForm}
              disabled={
                saving ||
                !form.storeName.trim() ||
                !form.storeLocation.trim() ||
                (!editingStore && !numericUserId)
              }
              className="w-full bg-blue-600 text-white font-bold text-sm py-4 rounded-full mt-6 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <Check size={15} />
              {saving ? "Saving..." : editingStore ? "Save changes" : "Add store"}
            </button>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-5 sm:p-8">
          <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-xs shadow-2xl">
            <div className="bg-red-50 rounded-full w-12 h-12 flex items-center justify-center mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Remove store</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to remove{" "}
              <strong className="text-gray-900">{deleteTarget.full_name}</strong>? This cannot
              be undone.
            </p>

            {actionError && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mb-4">
                {actionError}
              </p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  if (deleting) return;
                  setDeleteTarget(null);
                  setActionError("");
                }}
                disabled={deleting}
                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-full text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteStore}
                disabled={deleting || !numericUserId}
                className="flex-1 bg-red-500 text-white font-bold py-3 rounded-full text-sm disabled:opacity-50"
              >
                {deleting ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}